import { createFileRoute } from "@tanstack/react-router";
import assert from "assert";
import { DM_Masonry_BrickKing } from "common/materials/library/designMaterialPath";
import type { MaterialSrc } from "common/materials/materialSrc";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { EnvPreset } from "gfx/EnvPreset";
import type { HatchMaterialCache } from "gfx/hatchMaterialLoader";
import { getCachedLibraryHatchMaterial } from "gfx/hatchMaterialLoader";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { designMaterialNew } from "gfx/materials/designMaterial";
import { getHatchTextureMaterialFromThreeMaterial } from "gfx/materials/designMaterialHatchTexture";
import { defaultDesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import type { DesignMaterialParams } from "gfx/materials/designMaterialTypes";
import { createThreeMaterialFromHatchMaterial } from "gfx/materials/hatchTextureMaterial";
import { usePreloadedMaterialTextures } from "gfx/materials/usePreloadedMaterialTextures";
import { envPresetSceneLights } from "gfx/sceneLights";
import { DoubleSidedPlaneGeometry } from "gfx/threejs/higharc/DoubleSidedPlaneGeometry";
import { RenderParamsSource } from "gfx/threejs/higharc/RenderParamsSource";
import { UVTransformVertexShader } from "gfx/threejs/higharc/UVVertexShader";
import THREEViewer from "gfx/THREEViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

const cachedLibraryHatchMaterials: HatchMaterialCache = {};

enum TestMaterialType {
  ID = "ID",
  SRC = "SRC",
}

interface TestHatchMaterialId {
  type: TestMaterialType.ID;
  hatchMaterialId: HatchMaterialId;
}

interface TestHatchMaterialSrc {
  type: TestMaterialType.SRC;
  materialSrc: MaterialSrc;
}

type TestHatchMaterial = TestHatchMaterialId | TestHatchMaterialSrc;
type TestLoadedMaterial = TestHatchMaterialId | DesignMaterialParams;

function newTestId(hatchMaterialId: HatchMaterialId): TestHatchMaterialId {
  return { type: TestMaterialType.ID, hatchMaterialId };
}

function newTestSrc(materialSrc: MaterialSrc): TestHatchMaterialSrc {
  return { type: TestMaterialType.SRC, materialSrc };
}

function getInitialTestMaterials(): TestHatchMaterial[] {
  const materialSrcs: TestHatchMaterial[] = [
    newTestSrc(DM_Masonry_BrickKing.L.Brick_Acme.Brick_Acme_AdobeWells),
    newTestId(HatchMaterialId.Vertical_Lines),
    newTestId(HatchMaterialId.Horizontal_Lines),
    newTestId(HatchMaterialId.Diagonal_CrossHatch),
  ];
  return materialSrcs;
}

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 650,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 2000,
};

const MESH_SIZE = 50;
const MESH_SPACING = MESH_SIZE * 1.35;
const MESH_SPACING_Y = MESH_SIZE * 1.05;

let planeGeo: THREE.BufferGeometry;

function createGeoIfNeeded() {
  if (planeGeo !== undefined) {
    return;
  }
  planeGeo = new DoubleSidedPlaneGeometry(MESH_SIZE * 0.8, MESH_SIZE, 1, 1);
  planeGeo.userData.isCached = true;
  const uv = (planeGeo.getAttribute("uv") as THREE.BufferAttribute).array as Float32Array;
  for (let i = 0, l = uv.length; i < l; i++) {
    uv[i] *= MESH_SIZE;
  }
}

function HatchingTestPage() {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<THREEViewer | null>(null);

  const { loadTextureUrl, texturesReady } = usePreloadedMaterialTextures();
  const displayMeshesRef = useRef<THREE.Mesh[] | undefined>(undefined);

  const [testMaterials] = useState<TestHatchMaterial[]>(getInitialTestMaterials());

  function reinitViewer() {
    if (viewerRef.current) {
      // Dispose the old viewer so that shaders will be reloaded - this page exists mostly for shader development.
      viewerRef.current.dispose();
    }

    if (!viewerContainerRef.current) {
      return;
    }

    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.WHITEBOX],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    const viewer = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
        useMSAA: false,
      },
    });

    viewer.viewerRenderer.ssfxParamsManager?.setRenderParameters({
      screenRenderParams: {
        ssaaEnabled: false,
      },
      source: RenderParamsSource.TESTING,
      id: 0,
    });

    void (async () => {
      viewerRef.current = viewer;
      await renderMaterials();
    })();
  }

  async function reinitScene() {
    if (!viewerRef.current) {
      return;
    }
    viewerRef.current.clear();

    await renderMaterials();
  }

  function removeMeshes() {
    const displayMeshes = displayMeshesRef.current;
    if (displayMeshes === undefined) {
      return;
    }
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }
    for (let meshIndex = 0; meshIndex < displayMeshes.length; ++meshIndex) {
      const mesh = displayMeshes[meshIndex];
      viewer.scene.remove(mesh);
    }
    displayMeshesRef.current = [];
  }

  async function renderMaterials() {
    if (!viewerRef.current || !texturesReady) {
      return;
    }

    const paramsList: TestLoadedMaterial[] = [];
    for (const testMaterial of testMaterials) {
      if (testMaterial.type === "ID") {
        paramsList.push(testMaterial);
      } else {
        const materialParams: DesignMaterialParams | null =
          await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(testMaterial.materialSrc, true);
        assert(materialParams);
        paramsList.push(materialParams);
      }
    }

    await renderMaterialParams(paramsList);
  }

  async function renderMaterialParams(paramsList: TestLoadedMaterial[]) {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }
    const setupMesh = (mesh: THREE.Mesh) => {
      mesh.updateMatrixWorld();
      viewer.scene.add(mesh);
      displayMeshes.push(mesh);
    };

    createGeoIfNeeded();

    removeMeshes();

    if (displayMeshesRef.current === undefined) {
      displayMeshesRef.current = [];
    }
    const displayMeshes = displayMeshesRef.current;

    // Put the material meshes in a 2D lattice.
    const materialsPerRow = 3;
    const materialRows = paramsList.length * 2;

    if (!viewer.viewerRenderer.hasSSFXRenderer) {
      return;
    }

    for (let paramsIndex = 0, paramsL = paramsList.length; paramsIndex < paramsL; ++paramsIndex) {
      let material: THREE.ShaderMaterial;

      const testMaterial = paramsList[paramsIndex];

      if (testMaterial.type === TestMaterialType.ID) {
        const libraryMaterial = await getCachedLibraryHatchMaterial(
          testMaterial.hatchMaterialId,
          cachedLibraryHatchMaterials,
        );
        material = createThreeMaterialFromHatchMaterial(libraryMaterial, 2);
      } else {
        const designMaterial = await designMaterialNew(
          testMaterial,
          viewer.viewerRenderer.renderer,
          loadTextureUrl,
          viewer.hasSSFXRenderer,
          undefined,
          {
            ...defaultDesignMaterialLoadOptions,
          },
        );
        material = getHatchTextureMaterialFromThreeMaterial(designMaterial, 2);
      }

      {
        const index = paramsIndex * materialsPerRow;
        const posX = ((index % materialsPerRow) - materialsPerRow * 0.5 + 0.5) * MESH_SPACING;
        const posY = (Math.floor(index / materialsPerRow) - materialRows * 0.5 + 0.5) * MESH_SPACING_Y;

        const planeMesh = new THREE.Mesh(planeGeo, material);
        planeMesh.position.set(posX - MESH_SIZE * 0.25, posY, 0);
        setupMesh(planeMesh);
      }

      {
        const texture = material.uniforms.tSdf.value as THREE.Texture;
        const maxDist = material.uniforms.maxDist.value;

        const index = paramsIndex * materialsPerRow + 1;
        const posX = ((index % materialsPerRow) - materialsPerRow * 0.5 + 0.5) * MESH_SPACING;
        const posY = (Math.floor(index / materialsPerRow) - materialRows * 0.5 + 0.5) * MESH_SPACING_Y;
        const debugMaterial = new THREE.ShaderMaterial({
          uniforms: {
            uvTransform: { value: material.uniforms.tSdf.value.matrix },
            tSdf: { value: texture },
            repeat: { value: texture.repeat },
            maxDist: { value: maxDist },
          },
          vertexShader: UVTransformVertexShader,
          fragmentShader: /* glsl */ `
varying vec2 vUv;

uniform sampler2D tSdf;
uniform vec2 repeat;
uniform float maxDist;

const float distScale = 0.1;

void main() {
  float dx = dFdx(vUv.x) / (repeat.x);
  float dy = dFdy(vUv.y) / (repeat.y);
  float d2 = max(dx, dy);
  float dist = abs(texture2D(tSdf, vUv).r * 2.0 - 1.0);
  float sDist = (dist * maxDist) / d2;
  gl_FragColor = vec4(1.0 - vec3(sDist * distScale), 1.0);
}
`,
        });

        const debugMesh = new THREE.Mesh(planeGeo, debugMaterial);
        debugMesh.position.set(posX - MESH_SIZE * 0.25, posY, 0);
        setupMesh(debugMesh);
      }

      {
        const texture = material.uniforms.tSdf.value as THREE.Texture;

        const index = paramsIndex * materialsPerRow + 2;
        const posX = ((index % materialsPerRow) - materialsPerRow * 0.5 + 0.5) * MESH_SPACING;
        const posY = (Math.floor(index / materialsPerRow) - materialRows * 0.5 + 0.5) * MESH_SPACING_Y;
        const debugMaterial = new THREE.ShaderMaterial({
          uniforms: {
            uvTransform: { value: material.uniforms.tSdf.value.matrix },
            tSdf: { value: texture },
            repeat: { value: texture.repeat },
          },
          vertexShader: UVTransformVertexShader,
          fragmentShader: /* glsl */ `
varying vec2 vUv;

uniform sampler2D tSdf;
uniform vec2 repeat;

void main() {
  float dist = abs(texture2D(tSdf, vUv).r * 2.0 - 1.0);
  gl_FragColor = vec4(1.0 - vec3(dist), 1.0);
}
`,
        });

        const debugMesh = new THREE.Mesh(planeGeo, debugMaterial);
        debugMesh.position.set(posX - MESH_SIZE * 0.25, posY, 0);
        setupMesh(debugMesh);
      }
    }
  }

  useEffect(() => {
    reinitViewer();
  }, []);

  useEffect(() => {
    void reinitScene();
  }, [viewerRef, testMaterials, texturesReady]);

  return (
    <>
      <Head>
        <title>{getPageTitle("Hatching rendering test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div
        style={{ position: "absolute", top: 0, right: 0, left: 0, height: "100%", overflow: "hidden" }}
        ref={viewerContainerRef}
      />
    </>
  );
}

export const Route = createFileRoute("/debug/hatching-test")({
  component: withAuthentication(HatchingTestPage, { superAdmin: true }),
});
